import { nest } from "d3-collection";

import React, { Component } from "react";
import PropTypes from "prop-types";

import GenericChartComponent from "../GenericChartComponent";
import { getAxisCanvas } from "../GenericComponent";

import { hexToRGBA, isDefined, functor, head } from "../utils";

/**
 * CandleVolumeSeries - Candle chart where width is proportional to volume
 *
 * Based on ChartIQ's volume candle implementation where candles with more
 * volume are rendered wider than candles with less volume.
 */
class CandleVolumeSeries extends Component {
  constructor(props) {
    super(props);
    this.renderSVG = this.renderSVG.bind(this);
    this.drawOnCanvas = this.drawOnCanvas.bind(this);
  }

  drawOnCanvas(ctx, moreProps) {
    drawOnCanvas(ctx, this.props, moreProps);
  }

  renderSVG(moreProps) {
    const { className, wickClassName, candleClassName } = this.props;
    const {
      xScale,
      chartConfig: { yScale },
      plotData,
      xAccessor,
    } = moreProps;

    const candleData = getVolumeBasedCandleData(
      this.props,
      xAccessor,
      xScale,
      yScale,
      plotData,
    );

    return (
      <g className={className}>
        <g className={wickClassName} key="wicks">
          {getWicksSVG(candleData)}
        </g>
        <g className={candleClassName} key="candles">
          {getCandlesSVG(this.props, candleData)}
        </g>
      </g>
    );
  }

  render() {
    const { clip } = this.props;
    return (
      <GenericChartComponent
        clip={clip}
        svgDraw={this.renderSVG}
        canvasDraw={this.drawOnCanvas}
        canvasToDraw={getAxisCanvas}
        drawOn={["pan", "drag"]}
      />
    );
  }
}

CandleVolumeSeries.propTypes = {
  className: PropTypes.string,
  wickClassName: PropTypes.string,
  candleClassName: PropTypes.string,
  classNames: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  fill: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  stroke: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  wickStroke: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  yAccessor: PropTypes.func,
  clip: PropTypes.bool,
  opacity: PropTypes.number,
  candleStrokeWidth: PropTypes.number,
  minCandleWidth: PropTypes.number,
  maxCandleWidthRatio: PropTypes.number,
};

CandleVolumeSeries.defaultProps = {
  className: "react-stockcharts-candlevolume",
  wickClassName: "react-stockcharts-candlevolume-wick",
  candleClassName: "react-stockcharts-candlevolume-candle",
  yAccessor: (d) => ({
    open: d.open,
    high: d.high,
    low: d.low,
    close: d.close,
  }),
  classNames: (d) => (d.close > d.open ? "up" : "down"),
  wickStroke: "#687177",
  fill: (d) => (d.close > d.open ? "#8cc176" : "#c53717"),
  stroke: "#000000",
  candleStrokeWidth: 0.5,
  opacity: 1,
  clip: true,
  minCandleWidth: 2,
  maxCandleWidthRatio: 0.5, // Max 50% of available width for any single candle
};

function getWicksSVG(candleData) {
  const wicks = candleData.map((each, idx) => {
    const d = each.wick;
    return (
      <path
        key={idx}
        className={each.className}
        stroke={d.stroke}
        d={`M${d.x},${d.y1} L${d.x},${d.y2} M${d.x},${d.y3} L${d.x},${d.y4}`}
      />
    );
  });

  return wicks;
}

function getCandlesSVG(props, candleData) {
  const { opacity, candleStrokeWidth } = props;

  const candles = candleData.map((d, idx) => {
    if (d.width <= 1)
      return (
        <line
          className={d.className}
          key={idx}
          x1={d.x}
          y1={d.y}
          x2={d.x}
          y2={d.y + d.height}
          stroke={d.fill}
        />
      );
    else if (d.height === 0)
      return (
        <line
          key={idx}
          x1={d.x}
          y1={d.y}
          x2={d.x + d.width}
          y2={d.y + d.height}
          stroke={d.fill}
        />
      );
    return (
      <rect
        key={idx}
        className={d.className}
        fillOpacity={opacity}
        x={d.x}
        y={d.y}
        width={d.width}
        height={d.height}
        fill={d.fill}
        stroke={d.stroke}
        strokeWidth={candleStrokeWidth}
      />
    );
  });
  return candles;
}

function drawOnCanvas(ctx, props, moreProps) {
  const { opacity, candleStrokeWidth } = props;
  const {
    xScale,
    chartConfig: { yScale },
    plotData,
    xAccessor,
  } = moreProps;

  const candleData = getVolumeBasedCandleData(
    props,
    xAccessor,
    xScale,
    yScale,
    plotData,
  );

  // Draw wicks
  const wickNest = nest()
    .key((d) => d.wick.stroke)
    .entries(candleData);

  wickNest.forEach((outer) => {
    const { key, values } = outer;
    ctx.strokeStyle = key;
    ctx.fillStyle = key;
    values.forEach((each) => {
      const d = each.wick;
      ctx.fillRect(d.x - 0.5, d.y1, 1, d.y2 - d.y1);
      ctx.fillRect(d.x - 0.5, d.y3, 1, d.y4 - d.y3);
    });
  });

  // Draw candles
  const candleNest = nest()
    .key((d) => d.stroke)
    .key((d) => d.fill)
    .entries(candleData);

  candleNest.forEach((outer) => {
    const { key: strokeKey, values: strokeValues } = outer;
    if (strokeKey !== "none") {
      ctx.strokeStyle = strokeKey;
      ctx.lineWidth = candleStrokeWidth;
    }
    strokeValues.forEach((inner) => {
      const { key, values } = inner;
      const fillStyle = head(values).width <= 1 ? key : hexToRGBA(key, opacity);
      ctx.fillStyle = fillStyle;

      values.forEach((d) => {
        if (d.width <= 1) {
          ctx.fillRect(d.x - 0.5, d.y, 1, d.height);
        } else if (d.height === 0) {
          ctx.fillRect(d.x, d.y - 0.5, d.width, 1);
        } else {
          ctx.fillRect(d.x, d.y, d.width, d.height);
          if (strokeKey !== "none") ctx.strokeRect(d.x, d.y, d.width, d.height);
        }
      });
    });
  });
}

/**
 * Calculate candle data with volume-proportional widths
 *
 * Algorithm (based on ChartIQ legacy):
 * 1. Calculate total volume
 * 2. Calculate per-bar width as proportion of total
 * 3. Accumulate X positions
 */
function getVolumeBasedCandleData(props, xAccessor, xScale, yScale, plotData) {
  const {
    wickStroke: wickStrokeProp,
    minCandleWidth,
    maxCandleWidthRatio,
  } = props;
  const wickStroke = functor(wickStrokeProp);

  const { classNames, fill: fillProp, stroke: strokeProp, yAccessor } = props;
  const className = functor(classNames);
  const fill = functor(fillProp);
  const stroke = functor(strokeProp);

  // Get visible range
  const xRange = xScale.range();
  const availableWidth = Math.abs(xRange[1] - xRange[0]);

  // Filter valid data points (those with close values)
  const validData = plotData.filter((d) => isDefined(yAccessor(d).close));

  if (validData.length === 0) {
    return [];
  }

  // Phase 1: Calculate total volume
  let totalVolume = 0;
  for (let i = 0; i < validData.length; i++) {
    const d = validData[i];
    const volume = d.volume || 0;
    totalVolume += volume;
  }

  // Fallback: if no volume data, use uniform width
  if (totalVolume === 0) {
    const uniformWidth = availableWidth / validData.length;
    return getUniformCandleData(
      props,
      xAccessor,
      xScale,
      yScale,
      validData,
      uniformWidth,
    );
  }

  // Calculate max width cap (prevent single candle from dominating)
  const maxWidth = availableWidth * maxCandleWidthRatio;

  // Phase 2: Calculate per-bar widths and accumulated positions
  // First pass: calculate raw widths
  const rawWidths = [];
  let totalRawWidth = 0;

  for (let i = 0; i < validData.length; i++) {
    const d = validData[i];
    const volume = d.volume || 0;
    let candleWidth;

    if (volume > 0) {
      candleWidth = availableWidth * (volume / totalVolume);
      // Apply minimum and maximum constraints
      candleWidth = Math.max(candleWidth, minCandleWidth);
      candleWidth = Math.min(candleWidth, maxWidth);
    } else {
      candleWidth = minCandleWidth; // fallback for zero volume
    }

    rawWidths.push(candleWidth);
    totalRawWidth += candleWidth;
  }

  // Normalize widths to fit available width
  const scaleFactor = availableWidth / totalRawWidth;

  // Build candle data with accumulated positions
  const candles = [];
  let accumOffset = xRange[0]; // Start from left edge

  for (let i = 0; i < validData.length; i++) {
    const d = validData[i];
    const ohlc = yAccessor(d);

    // Calculate scaled width
    const candleWidth = rawWidths[i] * scaleFactor;
    const halfWidth = candleWidth / 2;

    // X position is center of candle
    const x = accumOffset + halfWidth;

    // Y calculations
    const y = Math.round(yScale(Math.max(ohlc.open, ohlc.close)));
    const height = Math.round(Math.abs(yScale(ohlc.open) - yScale(ohlc.close)));

    candles.push({
      x: Math.round(x - halfWidth), // Left edge of candle rect
      y: y,
      wick: {
        stroke: wickStroke(d),
        x: Math.round(x), // Center for wick
        y1: Math.round(yScale(ohlc.high)),
        y2: y,
        y3: y + height,
        y4: Math.round(yScale(ohlc.low)),
      },
      height: height,
      width: Math.max(1, Math.round(candleWidth)),
      className: className(ohlc),
      fill: fill(d),
      stroke: stroke(d),
      direction: ohlc.close - ohlc.open,
    });

    // Move to next candle position
    accumOffset += candleWidth;
  }

  return candles;
}

/**
 * Fallback function for uniform width candles (when no volume data)
 */
function getUniformCandleData(
  props,
  xAccessor,
  xScale,
  yScale,
  validData,
  uniformWidth,
) {
  const { wickStroke: wickStrokeProp } = props;
  const wickStroke = functor(wickStrokeProp);

  const { classNames, fill: fillProp, stroke: strokeProp, yAccessor } = props;
  const className = functor(classNames);
  const fill = functor(fillProp);
  const stroke = functor(strokeProp);

  const xRange = xScale.range();
  const candles = [];
  let accumOffset = xRange[0];

  for (let i = 0; i < validData.length; i++) {
    const d = validData[i];
    const ohlc = yAccessor(d);

    const halfWidth = uniformWidth / 2;
    const x = accumOffset + halfWidth;

    const y = Math.round(yScale(Math.max(ohlc.open, ohlc.close)));
    const height = Math.round(Math.abs(yScale(ohlc.open) - yScale(ohlc.close)));

    candles.push({
      x: Math.round(x - halfWidth),
      y: y,
      wick: {
        stroke: wickStroke(d),
        x: Math.round(x),
        y1: Math.round(yScale(ohlc.high)),
        y2: y,
        y3: y + height,
        y4: Math.round(yScale(ohlc.low)),
      },
      height: height,
      width: Math.max(1, Math.round(uniformWidth)),
      className: className(ohlc),
      fill: fill(d),
      stroke: stroke(d),
      direction: ohlc.close - ohlc.open,
    });

    accumOffset += uniformWidth;
  }

  return candles;
}

export default CandleVolumeSeries;
